import React, { useContext, useEffect, useState } from 'react';
import { DataContext } from '../context/DataContext';
import { TradingData } from '../helpers/CSVParser';
import TradingMetricsCalculator from '../helpers/TradingMetricsCalculator';

import { Table, TableBody, TableCell, TableContainer, TableRow, Typography, Tooltip as MuiTooltip, useTheme, Paper } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { OverallTradingMetrics } from '../interfaces/Metrics';
import TradingMetricsSmall from '../components/TradingMetricsSmall';
import { LoadingContext } from '../context/LoadingContext';
import { SettingsContext } from '../context/SettingsContext';
import { useTranslation } from '../context/TranslationContext';

interface GraphData {
  title: string;
  data: { datetime: Date; drawdown: number }[];
}

const DrawdownGraphs: React.FC = () => {
    const { dataset } = useContext(DataContext);
    const { loading, setLoading } = useContext(LoadingContext);
    const { collapsed, getLocale, showCurrencySymbol, getCurrencySymbol } = useContext(SettingsContext);
    const { t } = useTranslation();
    const [allDataGraph, setAllDataGraph] = useState<GraphData>({ title: "", data: [] });
    const [otherDataGraph, setOtherDataGraph] = useState<GraphData[]>([]);
    const [accumulatedMetrics, setAccumulatedMetrics] = useState<OverallTradingMetrics>();
    const [otherMetrics, setOtherMetrics] = useState<OverallTradingMetrics[]>();
    const [chartWidth, setChartWidth] = useState(window.innerWidth * (collapsed ? 0.52 : 0.37));
    const chartHeight = 300;
    const theme = useTheme();

    const formatValue = (value: number, decimals: number) => {
        const formattedValue = value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        if (showCurrencySymbol) {
            return `${formattedValue}\u00A0${getCurrencySymbol()}`;
        }
        return formattedValue;
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString(getLocale());
    };

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <MuiTooltip title="" placement="top">
                    <Paper style={{ padding: '10px', border: `1px solid ${theme.palette.divider}` }}>
                        {payload.map((entry: any, index: number) => (
                            entry.value !== 0 && (
                                <p key={`item-${index}`} style={{ color: entry.color }}>
                                    <Typography variant='subtitle2' color='primary'>{t("Date")}: {formatDate(new Date(entry.payload.datetime))}</Typography>
                                    <Typography>{`${formatValue(entry.value, 2)}`}</Typography>
                                </p>
                            )
                        ))}
                    </Paper>
                </MuiTooltip>
            );
        }
        return null;
    };

    useEffect(() => {
            const handleResize = () => {
                const factor = collapsed ? 0.52 : 0.37;
                setChartWidth(window.innerWidth * factor);
            };
    
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

    useEffect(() => {
        const factor = collapsed ? 0.52 : 0.37;
        setChartWidth(window.innerWidth * factor);
    }, [collapsed]);

    useEffect(() => {
        setLoading(true);
        setTimeout(() => { // Simulate a delay for the loading spinner
            const filesToConsider = dataset.files.filter(file => file.include === true);
            if (filesToConsider.length === 0) {
                setLoading(false);
                return;
            }
            let initialCapital = 0;
            let allTrades: TradingData[] = [];
            dataset.files.filter(file => file.include === true).forEach(file => {
                initialCapital += file.initialCapital;
                // filter parsedData by customStartDate and customEndDate if useAllTrades is false
                let filteredData = file.parsedData.filter(trade => trade.type.toLowerCase().includes('exit'));
                if (dataset.useDeepBacktest && dataset.customStartDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
                    });
                }
                if (dataset.useDeepBacktest && dataset.customEndDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
                    });
                }
                allTrades = [...allTrades, ...filteredData];
            });

            allTrades = TradingMetricsCalculator.recalculateTradeList(allTrades, initialCapital);
            allTrades = allTrades.filter(trade => trade.profit < 0); // Filter out trades for drawdown only
            setAccumulatedMetrics(new TradingMetricsCalculator(allTrades, true, initialCapital).calculateOverallMetrics());
            
            setAllDataGraph({
              title: "All",
              data: allTrades.map(trade => ({ datetime: new Date(trade.datetime), drawdown: trade.profit }))
            });
            const otherDataGraphs: GraphData[] = [];
            const otherMetrics: OverallTradingMetrics[] = [];
            dataset.files.forEach(file => {
                if (file.include) {
                    // filter parsedData by customStartDate and customEndDate if useAllTrades is false
                    let filteredData = file.parsedData.filter(trade => trade.type.toLowerCase().includes('exit'));
                    if (dataset.useDeepBacktest && dataset.customStartDate) {
                        filteredData = filteredData.filter(trade => {
                            const tradeDate = new Date(trade.datetime);
                            return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
                        });
                    }
                    if (dataset.useDeepBacktest && dataset.customEndDate) {
                        filteredData = filteredData.filter(trade => {
                            const tradeDate = new Date(trade.datetime);
                            return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
                        });
                    }
                    let trades = TradingMetricsCalculator.recalculateTradeList(filteredData, file.initialCapital);
                    trades = filteredData.filter(trade => trade.profit < 0); // Filter out trades for drawdown only
                    otherDataGraphs.push({
                        title: file.customId,
                        data: trades.map(trade => ({ datetime: new Date(trade.datetime), drawdown: trade.profit }))
                    });
                    otherMetrics.push(new TradingMetricsCalculator(trades, true, file.initialCapital).calculateOverallMetrics());
                }
            });
            setOtherDataGraph(otherDataGraphs);
            setOtherMetrics(otherMetrics);
            setLoading(false);
        }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataset]);

    const tableRowHeight = '400px';

    if (loading) return <></>;
    return (
        <>
            <Typography variant="h4" gutterBottom>
                {t('Drawdown Graphs')}
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableBody>
                        <TableRow style={{ height: tableRowHeight }}>
                            <TableCell>
                                <Typography variant="h6" gutterBottom>
                                    {allDataGraph.title}
                                </Typography>
                                <Paper elevation={2} style={{ padding: '16px', height: '100%', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.04),rgba(26, 83, 48, 0.01))' }}>             
                                    <AreaChart
                                        data={allDataGraph.data}
                                        height={chartHeight}
                                        width={chartWidth}
                                        margin={{
                                            top: 0,
                                            right: 0,
                                            left: 35,
                                            bottom: 0,
                                        }}      
                                    >
                                        <defs>
                                            <linearGradient id="colorGraph" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#ff0000" stopOpacity={0.6} />
                                                <stop offset="95%" stopColor="#ff0000" stopOpacity={0.25} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="datetime" tickFormatter={(tick) => formatDate(new Date(tick))} />
                                        <YAxis tickFormatter={(tick) => formatValue(tick, 2)} />
                                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }} />
                                        <Area type="monotone" dataKey="drawdown" stroke="#ff0000" fill="url(#colorGraph)" strokeWidth={2} />
                                    </AreaChart>
                                </Paper>
                            </TableCell>
                            <TableCell>
                                {accumulatedMetrics && <TradingMetricsSmall metrics={accumulatedMetrics} />}
                            </TableCell>
                        </TableRow>

                        {otherDataGraph.map((graphData, index) => (
                            <TableRow key={index} style={{ height: tableRowHeight }}>
                                <TableCell>
                                    <Typography variant="h6" gutterBottom>
                                        {graphData.title}
                                    </Typography>
                                    <Paper elevation={2} style={{ padding: '16px', height: '100%', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.04),rgba(26, 83, 48, 0.01))' }}>
                                        <AreaChart
                                            data={graphData.data}
                                            height={chartHeight}
                                            width={chartWidth}
                                            margin={{
                                                top: 0,
                                                right: 0,
                                                left: 35,
                                                bottom: 0,
                                            }}      
                                        >
                                            <defs>
                                                <linearGradient id="colorGraph" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#ff0000" stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor="#ff0000" stopOpacity={0.25} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="datetime" tickFormatter={(tick) => formatDate(new Date(tick))} />
                                            <YAxis tickFormatter={(tick) => formatValue(tick, 2)} />
                                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }} />
                                            <Area type="monotone" dataKey="drawdown" stroke="#ff0000" fill="url(#colorGraph)" strokeWidth={2} />
                                        </AreaChart>
                                    </Paper>
                                </TableCell>
                                <TableCell>
                                    {otherMetrics && otherMetrics[index] && <TradingMetricsSmall metrics={otherMetrics[index]} />}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

export default DrawdownGraphs;
